import { useState } from "react";
import { StickyNote } from "lucide-react";

import { COMPOSER_BUTTON, COMPOSER_FIELD, COMPOSER_LEAD_SLOT } from "@/components/todos/todo-composer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TAP_AREA } from "@/lib/tap-area";
import { cn } from "@/lib/utils";

/**
 * A record's note box, drawn as the deal tray's compact note composer: the to-do composer's row in a border
 * (`COMPOSER_FIELD`), the note icon in its lead slot, one line at rest that grows on focus or once there is
 * text, and a small Add Note beside the box that shows only once text is typed. The surface owns the write:
 * `onAdd` resolves once the note is saved (the box then clears) and rejects to keep the draft.
 */
export function RecordNoteComposer({
  onAdd,
  pending,
  testIdPrefix,
  placeholder = "Add a note…",
}: {
  onAdd: (body: string) => Promise<unknown>;
  pending: boolean;
  /** `<prefix>-composer`, `<prefix>-input` and `<prefix>-submit`. */
  testIdPrefix: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const grown = focused || draft.trim().length > 0;

  const handleAdd = async () => {
    const trimmed = draft.trim();
    if (!trimmed || pending) return;
    try {
      await onAdd(trimmed);
      setDraft("");
    } catch {
      // The surface's mutation already toasts on error; the draft stays.
    }
  };

  return (
    <div
      className={cn("flex items-start gap-x-3 focus-within:border-ring", COMPOSER_FIELD)}
      data-testid={`${testIdPrefix}-composer`}
    >
      <span className={cn(COMPOSER_LEAD_SLOT, "h-8")} aria-hidden>
        <StickyNote className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <Textarea
          data-testid={`${testIdPrefix}-input`}
          aria-label="Note"
          placeholder={placeholder}
          value={draft}
          maxLength={4000}
          disabled={pending}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          rows={grown ? 2 : 1}
          className={cn(
            "min-h-8 resize-none border-0 bg-transparent px-0 py-1.5 text-base shadow-none focus-visible:ring-0 md:text-sm dark:bg-transparent",
            grown ? "min-h-14" : "h-8",
          )}
        />
      </div>
      <div className="flex h-8 shrink-0 items-center">
        {draft.trim() ? (
          <Button
            type="button"
            size="sm"
            data-testid={`${testIdPrefix}-submit`}
            className={cn(COMPOSER_BUTTON, "relative", TAP_AREA)}
            disabled={pending}
            onClick={handleAdd}
            aria-busy={pending}
          >
            {pending ? "Adding…" : "Add Note"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
